import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, MapPin, Loader2, CheckCircle2, AlertTriangle, Clock, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, getToken } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { useAuth } from "@/lib/auth";
import { mutate as offlineMutate, isOnline, setSimulateOffline, getSimulateOffline }
  from "@/offline/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

// ---------------------------------------------------------------------------
// Tiny helpers
// ---------------------------------------------------------------------------
function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function fmtDuration(sec) {
  if (!Number.isFinite(sec)) return "—";
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
}

async function compressToJpeg(blob, maxDim = 800, quality = 0.6) {
  const img = new Image();
  img.src = URL.createObjectURL(blob);
  await new Promise((r) => { img.onload = r; });
  let { width: w, height: h } = img;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  w = Math.round(w * scale); h = Math.round(h * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", quality));
}

async function uploadPhoto(blob, context = "attendance") {
  const form = new FormData();
  form.append("file", blob, `selfie-${Date.now()}.jpg`);
  form.append("context", context);
  const res = await fetch(`${API_BASE}/photos/upload`, {
    method: "POST", body: form,
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error(`upload failed: ${res.status}`);
  return await res.json();
}

// ---------------------------------------------------------------------------
// Main screen
// ---------------------------------------------------------------------------
export default function AttendanceScreen() {
  const navigate = useNavigate();
  const { refreshAttendance } = useAuth();
  const [settings, setSettings] = useState(null);
  const [today, setToday] = useState(null);
  const [gps, setGps] = useState(null);           // {lat, lng, accuracy_m}
  const [gpsErr, setGpsErr] = useState(null);
  const [selfie, setSelfie] = useState(null);     // {blob, url, ref?}
  const [selfieUploadError, setSelfieUploadError] = useState(null); // string | null
  const [busy, setBusy] = useState(false);
  const [simOffline, setSimOffline] = useState(getSimulateOffline());
  // Drop 6 · Item 5 — Day End 2-step confirm + blocker rendering.
  const [dayEndOpen, setDayEndOpen] = useState(false);
  const [dayEndBusy, setDayEndBusy] = useState(false);
  const [dayEndBlockers, setDayEndBlockers] = useState(null);
  // Phase 25B — Working type (IN_CITY / OUT_CITY) + mandatory City dropdown.
  const [workingTypes, setWorkingTypes] = useState([]);
  const [workingType, setWorkingType] = useState("");
  const [cities, setCities] = useState([]);
  const [cityId, setCityId] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [cityListOpen, setCityListOpen] = useState(false);
  // Free-text fallback for a city not in the (mapped-distributor-scoped)
  // list — mutually exclusive with cityId.
  const [cityManualMode, setCityManualMode] = useState(false);
  const [cityManual, setCityManual] = useState("");
  const [lookupErr, setLookupErr] = useState(null);
  const [lookupRetry, setLookupRetry] = useState(0);
  const clockRef = useRef(0);
  const [_, setTick] = useState(0);
  // ASM/SE only — a prior day left without Day End blocks a new punch-in
  // (see routes_attendance.py::punch_in). Checked proactively so the rep
  // sees this before filling in GPS/selfie, not just as a failed punch-in.
  const [pendingDayEnd, setPendingDayEnd] = useState(null); // {date} | null
  const [resolveDayEndOpen, setResolveDayEndOpen] = useState(false);
  const [resolveDayEndBusy, setResolveDayEndBusy] = useState(false);
  const [resolveDayEndBlockers, setResolveDayEndBlockers] = useState(null);

  const refreshDayEndStatus = useCallback(() => {
    api.get("/attendance/day-end-status")
       .then((r) => setPendingDayEnd(r.data?.pending ? { date: r.data.date } : null))
       .catch(() => {});
  }, []);

  // Poll settings once + today on mount
  useEffect(() => {
    api.get("/attendance/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/attendance/today").then((r) => setToday(r.data)).catch(() => {});
    refreshDayEndStatus();
  }, [refreshDayEndStatus]);

  // Phase 25B — Load working types + cities. Both time-bounded (5s) with graceful failure.
  // Cities come from /attendance/my-cities — scoped to the rep's actual
  // MAPPED distributors' cities (their real working area), falling back to
  // their zone only if they have no explicit distributor mapping. Plain
  // /geography/cities returned every city nationwide regardless of the
  // caller, and zone-wide alone was still far broader than where a rep
  // actually goes.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    setLookupErr(null);
    Promise.all([
      api.get("/lookups?category=working_type", { signal: controller.signal }),
      api.get("/attendance/my-cities", { signal: controller.signal }),
    ]).then(([wt, ct]) => {
      const wtItems = (wt.data?.items || wt.data || []).filter(x => x.is_active !== false);
      const ctItems = (ct.data?.items || ct.data || []);
      setWorkingTypes(wtItems);
      setCities(ctItems);
      setLookupErr(null);
      if (wtItems.length === 0) {
        setLookupErr("Working type list unavailable — contact admin.");
      }
    }).catch((e) => {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED" ||
          controller.signal.aborted) return;
      // eslint-disable-next-line no-console
      console.warn("attendance lookup fetch failed:", e?.message || e);
      setLookupErr("Working type or city list unavailable — contact admin.");
    }).finally(() => clearTimeout(timer));
    return () => { clearTimeout(timer); controller.abort(); };
  }, [lookupRetry]);

  // Live GPS via watchPosition
  useEffect(() => {
    if (!navigator.geolocation) { setGpsErr("Geolocation not supported"); return; }
    const id = navigator.geolocation.watchPosition(
      (p) => {
        setGps({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy });
        setGpsErr(null);
      },
      (err) => setGpsErr(err.message || "GPS unavailable"),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 5000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // Running clock (only when punched-in without punch-out)
  useEffect(() => {
    if (!today?.check_in_ts || today?.check_out_ts) return;
    const int = setInterval(() => { clockRef.current++; setTick((n) => n + 1); }, 1000);
    return () => clearInterval(int);
  }, [today?.check_in_ts, today?.check_out_ts]);

  const now = Date.now();
  const elapsedSec = today?.check_in_ts && !today?.check_out_ts
    ? Math.max(0, Math.floor((now - new Date(today.check_in_ts).getTime()) / 1000))
    : null;

  const gpsOk = gps && (!settings?.gps_required || true);
  const selfieOk = !!selfie?.ref || !settings?.selfie_required;
  const canPunchIn = !!settings && !today && !pendingDayEnd
      && (!settings.gps_required || !!gps)
      && (!settings.selfie_required || !!selfie?.ref)
      && !!workingType;   // Phase 25B — working_type required, city is optional
  // Hotfix — selfie no longer required at punch-out (same exemption as
  // visit-start elsewhere in this codebase); server no longer enforces it
  // for kind="out" either, so this just matches that.
  const canPunchOut = !!today && !today.check_out_ts && (!settings?.gps_required || !!gps);

  const captureSelfie = async () => {
    // Bug 3 (phase-2): CAMERA-ONLY. `capture="user"` hints the front camera;
    // some mobile browsers still fall back to the gallery. We accept the
    // capture attribute here (best effort); full gallery-block is enforced
    // in bugfix batch 3-6.
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*"; input.capture = "user";
    input.onchange = async () => {
      const f = input.files?.[0]; if (!f) return;
      // Bug 3 · Hard camera-only guard: reject files whose lastModified is
      // older than a fresh-capture window (60s). Live camera capture writes
      // `lastModified` to the moment of shutter; gallery uploads carry the
      // original photo timestamp. `capture="user"` is best-effort and some
      // mobile browsers ignore it — this guard closes the fallback.
      const FRESH_WINDOW_MS = 60_000;
      const ageMs = Date.now() - (f.lastModified || 0);
      if (!f.lastModified || ageMs > FRESH_WINDOW_MS) {
        toast.error("Please take a new selfie with the camera (gallery uploads are not allowed).");
        return;
      }
      setBusy(true);
      setSelfieUploadError(null);
      try {
        const small = await compressToJpeg(f);
        const url = URL.createObjectURL(small);
        setSelfie({ blob: small, url, ref: null });
        // Upload immediately if online; otherwise defer until punch-in drain
        if (isOnline() && !simOffline) {
          try {
            const r = await uploadPhoto(small);
            setSelfie((s) => ({ ...s, ref: r.ref }));
            toast.success("Selfie captured");
          } catch (upErr) {
            // Keep the local blob so user can retry without retaking.
            setSelfieUploadError(upErr.message || "Selfie upload failed");
            toast.error(upErr.message || "Selfie upload failed");
          }
        } else {
          toast.info("Selfie captured (will upload when online)");
        }
      } catch (e) {
        toast.error(e.message || "Selfie failed");
      } finally { setBusy(false); }
    };
    input.click();
  };

  const retryUploadSelfie = async () => {
    if (!selfie?.blob || busy) return;
    setBusy(true);
    setSelfieUploadError(null);
    try {
      const r = await uploadPhoto(selfie.blob);
      setSelfie((s) => ({ ...s, ref: r.ref }));
      toast.success("Selfie uploaded");
    } catch (upErr) {
      setSelfieUploadError(upErr.message || "Selfie upload failed");
      toast.error(upErr.message || "Selfie upload failed");
    } finally { setBusy(false); }
  };

  const doPunch = async (kind) => {
    if (busy) return;
    setBusy(true);
    try {
      // If selfie exists locally but no ref yet, upload now (unless offline)
      let selfieRef = selfie?.ref;
      if (selfie && !selfieRef && isOnline() && !simOffline) {
        try {
          const r = await uploadPhoto(selfie.blob);
          selfieRef = r.ref;
        } catch (e) {
          // Non-fatal — proceed if settings don't require selfie
          if (settings?.selfie_required) throw e;
        }
      }
      const body = {
        gps, selfie_ref: selfieRef,
        client_ts: new Date().toISOString(),
        beat_id: today?.check_in_beat_id || null,
        market_type_code: undefined,           // Phase 25B — dropped
        working_type_code: kind === "in" ? workingType : undefined,   // Phase 25B
        city_id: kind === "in" ? (cityManualMode ? "" : cityId) : undefined,   // Phase 25B
        city_manual: kind === "in" && cityManualMode ? cityManual.trim() : undefined,
        idempotency_key: (crypto.randomUUID?.() || `att-${Date.now()}`),
        offline_captured: !isOnline() || simOffline,
      };
      const path = kind === "in" ? "/attendance/punch-in" : "/attendance/punch-out";
      const r = await offlineMutate({
        method: "POST", path, body,
        entity_type: "attendance", offlineCapable: true,
      });
      if (r.immediate) {
        setToday(r.response);
        toast.success(kind === "in" ? "Punched in ✓" : "Punched out ✓");
        if (kind === "in") refreshAttendance();
      } else {
        // Optimistic offline state — server will assign the real ts on drain
        const stamp = new Date().toISOString();
        setToday((prev) => {
          if (kind === "in") {
            return { id: "pending", date: stamp.slice(0, 10),
                     check_in_ts: stamp, check_in_gps: gps,
                     check_in_selfie_ref: selfieRef, offline_captured: true,
                     status: "partial", _pending: true };
          }
          return { ...prev, check_out_ts: stamp, check_out_gps: gps,
                   check_out_selfie_ref: selfieRef,
                   working_seconds: prev?.check_in_ts ?
                     Math.max(0, Math.floor((Date.now() - new Date(prev.check_in_ts).getTime()) / 1000)) : 0,
                   status: "present", _pending: true };
        });
        toast.info(`Punch ${kind === "in" ? "in" : "out"} queued — will sync`);
      }
      setSelfie(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "day_end_pending") {
        // Race-condition safety net — the proactive check on load should
        // normally have already shown the banner before this point.
        setPendingDayEnd({ date: d.date });
        toast.error(d.message || `Complete Day End for ${d.date} first.`);
      } else {
        const msg = d || e.message || "Failed";
        toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
      }
    } finally { setBusy(false); }
  };

  const toggleSim = () => {
    const next = !simOffline; setSimulateOffline(next); setSimOffline(next);
  };

  // Drop 6 · Item 5 — Day End flow.
  // Order: /attendance/day-end first (fires expense/TA-DA + open-visit
  // blockers). Only when day-end passes do we call /attendance/punch-out
  // to record check_out_ts. If blockers fire we surface a structured
  // message; user remains in `state === "in_progress"` — nothing is lost.
  const runDayEnd = async () => {
    if (dayEndBusy) return;
    setDayEndBusy(true);
    setDayEndBlockers(null);
    try {
      // 1. Attempt Day End (blocker validation).
      try {
        await api.post("/attendance/day-end", { gps, remarks: null });
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (d?.error === "day_end_blocked" && Array.isArray(d.blockers)) {
          setDayEndBlockers(d.blockers);
          return;   // do NOT proceed to punch-out
        }
        if (d?.error === "already_ended_today") {
          // Day was already ended earlier — treat as no-op and let punch-out
          // proceed to close attendance if it's still open.
        } else {
          throw e;
        }
      }
      // 2. Record check-out (idempotent on repeat).
      await doPunch("out");
      setDayEndOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Day End failed";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally { setDayEndBusy(false); }
  };

  // Resolve a PAST unfinished day (blocking today's punch-in) — same
  // /attendance/day-end endpoint, just addressed at that date instead of
  // today. Same blockers (open visits, missing TA/DA or expense) apply,
  // scoped to that date, since that's what Day End itself always checks.
  const resolvePendingDayEnd = async () => {
    if (!pendingDayEnd || resolveDayEndBusy) return;
    setResolveDayEndBusy(true);
    setResolveDayEndBlockers(null);
    try {
      await api.post("/attendance/day-end", { date: pendingDayEnd.date, remarks: null });
      toast.success(`Day End completed for ${pendingDayEnd.date}`);
      setResolveDayEndOpen(false);
      refreshDayEndStatus();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "day_end_blocked" && Array.isArray(d.blockers)) {
        setResolveDayEndBlockers(d.blockers);
      } else {
        const msg = d || e.message || "Day End failed";
        toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
      }
    } finally { setResolveDayEndBusy(false); }
  };

  if (!settings) return <div className="p-6 text-neutral-500">Loading attendance…</div>;

  const state = !today ? "not_yet" : today.check_out_ts ? "done" : "in_progress";

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="attendance-screen">
      <button type="button" onClick={() => navigate("/attendance-calendar")}
              data-testid="attendance-month-view-link"
              className="w-full flex items-center justify-between rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm font-semibold text-[#6D2C91]">
        <span>View my attendance — whole month</span>
        <span aria-hidden>›</span>
      </button>
      {/* Sim-offline toggle for testers (dev aid) */}
      <div className="flex items-center justify-between text-[11px] text-neutral-500 px-1">
        <span>Simulate offline</span>
        <Switch checked={simOffline} onCheckedChange={toggleSim} data-testid="attendance-sim-offline" />
      </div>

      {/* State: not yet punched in */}
      {state === "not_yet" && (
        <>
          {pendingDayEnd && (
            <Card className="border-rose-300 bg-rose-50 rounded-2xl" data-testid="attendance-day-end-pending-banner">
              <CardContent className="p-4 space-y-2">
                <div className="text-sm font-bold text-rose-800">
                  Day End pending for {pendingDayEnd.date}
                </div>
                <p className="text-xs text-rose-700">
                  You didn't complete Day End for {pendingDayEnd.date} — punch-in is blocked until
                  that day is closed out.
                </p>
                <Button size="sm" onClick={() => setResolveDayEndOpen(true)}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="attendance-resolve-day-end-btn">
                  Complete Day End for {pendingDayEnd.date}
                </Button>
              </CardContent>
            </Card>
          )}

          <Card className="border-neutral-200 rounded-2xl" data-testid="attendance-state-not-yet">
            <CardContent className="p-6 space-y-3">
              <div className="text-xs uppercase tracking-widest text-neutral-500">Attendance</div>
              <h2 className="text-2xl font-bold text-neutral-900">Ready to start your day?</h2>
              <p className="text-sm text-neutral-500">Capture GPS + selfie, then punch in. Your working hours start from server time.</p>
              <div className="flex flex-wrap gap-2 text-[11px] pt-1">
                <span className={`px-2 py-1 rounded-full ${settings.gps_required ? "bg-neutral-100" : "bg-neutral-50 text-neutral-400"}`}>
                  GPS {settings.gps_required ? "required" : "optional"}
                </span>
                <span className={`px-2 py-1 rounded-full ${settings.selfie_required ? "bg-neutral-100" : "bg-neutral-50 text-neutral-400"}`}>
                  Selfie {settings.selfie_required ? "required" : "optional"}
                </span>
                <span className="px-2 py-1 rounded-full bg-neutral-100">Radius {settings.beat_radius_m}m</span>
              </div>
            </CardContent>
          </Card>

          <GpsCard gps={gps} gpsErr={gpsErr} />

          <SelfieCard
            selfie={selfie}
            onCapture={captureSelfie}
            busy={busy}
            uploadError={selfieUploadError}
            onRetry={retryUploadSelfie}
          />

          {/* Phase 25B — Working type toggle (IN_CITY / OUT_CITY) + mandatory City dropdown */}
          <Card className="border-neutral-200 rounded-2xl" data-testid="working-type-card">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-neutral-500">Today's working</div>
                <span className="text-[10px] text-rose-600 font-semibold">required</span>
              </div>

              {lookupErr ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-800 space-y-2"
                  data-testid="working-type-error">
                  <div className="font-medium flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> {lookupErr}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setLookupRetry(n => n + 1)}
                    data-testid="working-type-retry">Retry</Button>
                </div>
              ) : workingTypes.length === 0 ? (
                <div className="text-[11px] text-neutral-500 flex items-center gap-2"
                  data-testid="working-type-loading">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading working types…
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {workingTypes.map((wt) => {
                    const active = workingType === wt.code;
                    return (
                      <button
                        key={wt.code}
                        type="button"
                        onClick={() => setWorkingType(wt.code)}
                        data-testid={`working-type-${wt.code}`}
                        className={
                          "px-2.5 py-2.5 rounded-xl border text-sm font-semibold transition-all " +
                          (active
                            ? "border-[#6D2C91] bg-[#6D2C91] text-white shadow-sm"
                            : "border-neutral-200 bg-white text-neutral-700 hover:border-[#6D2C91]/50")
                        }
                      >
                        {wt.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {!lookupErr && workingTypes.length > 0 && (() => {
                const selectedCity = cities.find(c => c.id === cityId);
                const filteredCities = cities
                  .filter(c => !citySearch || (c.name || "").toLowerCase().includes(citySearch.toLowerCase()))
                  .slice(0, 30);
                const showList = cityListOpen && filteredCities.length > 0;
                return (
                <div className="space-y-1.5">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 pt-1 flex items-center justify-between">
                    <span>City <span className="text-neutral-400 normal-case">(optional)</span></span>
                    <button
                      type="button"
                      className="text-[11px] normal-case font-medium text-[#6D2C91] underline decoration-dotted"
                      onClick={() => {
                        setCityManualMode((v) => !v);
                        setCityId(""); setCitySearch(""); setCityListOpen(false);
                        setCityManual("");
                      }}
                      data-testid="attendance-city-manual-toggle"
                    >
                      {cityManualMode ? "Pick from list instead" : "City not listed? Type it"}
                    </button>
                  </div>
                  {cityManualMode ? (
                    <input
                      type="text"
                      placeholder="Type your city…"
                      value={cityManual}
                      onChange={(e) => setCityManual(e.target.value)}
                      className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:border-[#6D2C91]"
                      data-testid="attendance-city-manual-input"
                    />
                  ) : (
                  <>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search city…"
                      value={citySearch}
                      onFocus={() => setCityListOpen(true)}
                      onChange={(e) => {
                        setCitySearch(e.target.value);
                        setCityListOpen(true);
                        // Typing after selection unlocks re-selection.
                        if (cityId && (cities.find(c => c.id === cityId)?.name || "") !== e.target.value) {
                          setCityId("");
                        }
                      }}
                      className="w-full rounded-lg border border-neutral-200 px-3 py-2 pr-16 text-sm focus:outline-none focus:border-[#6D2C91]"
                      data-testid="attendance-city-search"
                      aria-autocomplete="list"
                      aria-expanded={showList}
                    />
                    {cityId && selectedCity && (
                      <button
                        type="button"
                        onClick={() => {
                          setCityId("");
                          setCitySearch("");
                          setCityListOpen(true);
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-neutral-500 hover:text-neutral-800 underline"
                        data-testid="attendance-city-clear"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {cityId && selectedCity && (
                    <div
                      className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1"
                      data-testid="attendance-city-selected"
                    >
                      <CheckCircle2 className="h-3 w-3" /> Selected: {selectedCity.name}
                    </div>
                  )}
                  {showList && (
                    <div className="max-h-40 overflow-y-auto rounded-lg border border-neutral-100"
                      data-testid="attendance-city-list">
                      {filteredCities.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          // Use onMouseDown to fire BEFORE the input's blur — this
                          // was the root cause: on mobile the input's blur closed
                          // the list before the button's click event dispatched.
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setCityId(c.id);
                            setCitySearch(c.name || "");
                            setCityListOpen(false);
                          }}
                          onClick={(e) => {
                            // Fallback for keyboard/enter activation.
                            e.preventDefault();
                            setCityId(c.id);
                            setCitySearch(c.name || "");
                            setCityListOpen(false);
                          }}
                          data-testid={`attendance-city-${c.id}`}
                          className={
                            "block w-full text-left px-3 py-1.5 text-sm border-b last:border-b-0 border-neutral-100 " +
                            (cityId === c.id ? "bg-[#6D2C91]/10 text-[#6D2C91] font-semibold" : "hover:bg-neutral-50")
                          }
                        >
                          {c.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {cities.length === 0 && (
                    <div className="text-[11px] text-neutral-400">
                      No cities mapped for you yet — leave this blank and punch in, or type it manually above.
                    </div>
                  )}
                  </>
                  )}
                </div>
                );
              })()}
            </CardContent>
          </Card>

          <Button
            onClick={() => doPunch("in")}
            disabled={busy || !canPunchIn}
            className="w-full h-14 text-lg tulsi-gradient text-white font-bold rounded-2xl"
            data-testid="attendance-punch-in"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
            Punch In
          </Button>
        </>
      )}

      {/* State: in progress */}
      {state === "in_progress" && (
        <>
          <Card className="tulsi-gradient text-white rounded-2xl" data-testid="attendance-state-in-progress">
            <CardContent className="p-6 space-y-2">
              <div className="text-[11px] uppercase tracking-widest opacity-80">You're on the clock</div>
              <div className="text-4xl font-bold" data-testid="attendance-elapsed">
                {fmtDuration(elapsedSec)}
              </div>
              <div className="text-sm opacity-90">Started at {fmtTime(today.check_in_ts)}</div>
              {today._pending && (
                <div className="text-[11px] bg-white/20 rounded-full px-2 py-1 inline-flex items-center gap-1 mt-2">
                  <Clock className="h-3 w-3" /> Offline — will sync
                </div>
              )}
              <RadiusFlag today={today} radius={settings.beat_radius_m} />
            </CardContent>
          </Card>

          <GpsCard gps={gps} gpsErr={gpsErr} />

          {/* Hotfix — selfie is no longer required (or offered) at
              punch-out at all, same exemption already applied to
              visit-start elsewhere in this codebase. Punch-in above still
              respects settings.selfie_required unchanged. */}

          <Button
            onClick={() => { setDayEndBlockers(null); setDayEndOpen(true); }}
            disabled={busy || (settings.checkout_required && !canPunchOut)}
            variant="outline"
            className="w-full h-14 text-lg font-bold rounded-2xl border-2 border-[#E5197F] text-[#E5197F] hover:bg-[#E5197F]/5"
            data-testid="attendance-day-end-btn"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <LogOut className="h-5 w-5 mr-2" />}
            Day End · Punch Out
          </Button>
          {/* Hidden legacy testid — preserved for pre-Drop-6 regression suites. */}
          <span data-testid="attendance-punch-out" style={{ display: "none" }} />
        </>
      )}

      {/* State: done */}
      {state === "done" && (
        <>
          <Card className="border-neutral-200 rounded-2xl" data-testid="attendance-state-done">
            <CardContent className="p-6 space-y-2 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
              <div className="text-xs uppercase tracking-widest text-neutral-500">Day complete</div>
              <div className="text-3xl font-bold text-neutral-900">
                Worked {fmtDuration(today.working_seconds)}
              </div>
              <div className="text-sm text-neutral-500">
                {fmtTime(today.check_in_ts)} → {fmtTime(today.check_out_ts)}
              </div>
              <RadiusFlag today={today} radius={settings.beat_radius_m} />
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <ThumbCard label="Punch-in" ref_={today.check_in_selfie_ref} />
            <DaySummaryCard punchOutTs={today.check_out_ts} />
          </div>
        </>
      )}

      {/* Drop 6 · Item 5 — Day End 2-step confirm + Item 4 expense gate. */}
      <Dialog open={dayEndOpen} onOpenChange={(v) => !v && setDayEndOpen(false)}>
        <DialogContent className="max-w-sm"
                        data-testid="attendance-day-end-confirm-dialog">
          <DialogHeader>
            <DialogTitle>End the day?</DialogTitle>
          </DialogHeader>
          {dayEndBlockers && dayEndBlockers.length > 0 ? (
            <div className="space-y-2"
                  data-testid="attendance-day-end-expense-gate-msg">
              <div className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Cannot end day yet
              </div>
              <ul className="text-sm text-neutral-700 list-disc pl-5 space-y-1">
                {dayEndBlockers.map((b) => (
                  <li key={b.code} data-testid={`day-end-blocker-${b.code}`}>
                    {b.message}
                  </li>
                ))}
              </ul>
              <div className="text-[11px] text-neutral-500 pt-1">
                Resolve the item(s) above and try again.
              </div>
            </div>
          ) : (
            <div className="text-sm text-neutral-700">
              Are you sure? Day End is final. You'll need admin approval to reopen.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline"
                    onClick={() => setDayEndOpen(false)}
                    data-testid="attendance-day-end-cancel">
              {dayEndBlockers && dayEndBlockers.length > 0 ? "Close" : "Cancel"}
            </Button>
            {!(dayEndBlockers && dayEndBlockers.length > 0) && (
              <Button onClick={runDayEnd} disabled={dayEndBusy}
                      className="bg-[#E5197F] text-white hover:bg-[#c11569]"
                      data-testid="attendance-day-end-confirm-yes">
                {dayEndBusy
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : null}
                Yes, End Day
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve a PAST unfinished day blocking today's punch-in. */}
      <Dialog open={resolveDayEndOpen} onOpenChange={(v) => !v && setResolveDayEndOpen(false)}>
        <DialogContent className="max-w-sm"
                        data-testid="attendance-resolve-day-end-dialog">
          <DialogHeader>
            <DialogTitle>Complete Day End for {pendingDayEnd?.date}?</DialogTitle>
          </DialogHeader>
          {resolveDayEndBlockers && resolveDayEndBlockers.length > 0 ? (
            <div className="space-y-2" data-testid="attendance-resolve-day-end-blockers">
              <div className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Cannot close that day yet
              </div>
              <ul className="text-sm text-neutral-700 list-disc pl-5 space-y-1">
                {resolveDayEndBlockers.map((b) => (
                  <li key={b.code} data-testid={`resolve-day-end-blocker-${b.code}`}>
                    {b.message}
                  </li>
                ))}
              </ul>
              {resolveDayEndBlockers.some((b) => b.code === "expense_not_submitted") && (
                <Button variant="outline" size="sm"
                        onClick={() => navigate(`/tada?date=${pendingDayEnd?.date}`)}
                        data-testid="attendance-resolve-day-end-goto-tada">
                  Submit TA/DA claim for {pendingDayEnd?.date}
                </Button>
              )}
              <div className="text-[11px] text-neutral-500 pt-1">
                Resolve the item(s) above (e.g. file that day's TA/DA claim or expense,
                close any still-open visit) and try again.
              </div>
            </div>
          ) : (
            <div className="text-sm text-neutral-700">
              This finalizes {pendingDayEnd?.date}. You'll need admin approval to reopen it afterward.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline"
                    onClick={() => setResolveDayEndOpen(false)}
                    data-testid="attendance-resolve-day-end-cancel">
              {resolveDayEndBlockers && resolveDayEndBlockers.length > 0 ? "Close" : "Cancel"}
            </Button>
            {!(resolveDayEndBlockers && resolveDayEndBlockers.length > 0) && (
              <Button onClick={resolvePendingDayEnd} disabled={resolveDayEndBusy}
                      className="bg-[#E5197F] text-white hover:bg-[#c11569]"
                      data-testid="attendance-resolve-day-end-confirm">
                {resolveDayEndBusy
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : null}
                Yes, End That Day
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function GpsCard({ gps, gpsErr }) {
  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid="attendance-gps-card">
      <CardContent className="p-4 flex items-center gap-3">
        <MapPin className={`h-5 w-5 ${gps ? "text-[#E5197F]" : "text-neutral-400"}`} />
        <div className="flex-1 text-sm">
          {gps ? (
            <>
              <div className="font-mono">{gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</div>
              <div className={`text-[11px] ${gps.accuracy_m > 100 ? "text-amber-600" : "text-neutral-500"}`}>
                Accuracy ±{Math.round(gps.accuracy_m)}m
                {gps.accuracy_m > 100 && " · try moving outdoors"}
              </div>
            </>
          ) : gpsErr ? (
            <div className="text-amber-700 text-[13px]">
              <AlertTriangle className="h-3 w-3 inline mr-1" /> {gpsErr}
            </div>
          ) : (
            <div className="text-neutral-500 text-[13px]">Locating…</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SelfieCard({ selfie, onCapture, busy, label = "Selfie", uploadError, onRetry }) {
  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid="attendance-selfie-card">
      <CardContent className="p-4 flex flex-col gap-3">
        <div className="flex items-center gap-3">
          {selfie?.url ? (
            <img src={selfie.url} alt="selfie preview"
                 className="h-16 w-16 rounded-full object-cover border border-neutral-200" />
          ) : (
            <div className="h-16 w-16 rounded-full bg-neutral-100 flex items-center justify-center">
              <Camera className="h-6 w-6 text-neutral-400" />
            </div>
          )}
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
            <div className="text-[13px] text-neutral-600">
              {selfie?.ref
                ? "Ready ✓"
                : uploadError
                  ? <span className="text-rose-600 font-medium">Upload failed</span>
                  : selfie
                    ? "Captured — uploading…"
                    : "Not yet"}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onCapture} disabled={busy}
                  data-testid="attendance-take-selfie">
            <Camera className="h-4 w-4 mr-1" /> {selfie ? "Retake" : "Take"}
          </Button>
        </div>
        {uploadError && !selfie?.ref && (
          <div
            className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-[12px] text-rose-800 flex items-start gap-2"
            data-testid="selfie-upload-error"
          >
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div className="flex-1 space-y-2">
              <div>
                <div className="font-medium">Selfie upload failed</div>
                <div className="opacity-80">{uploadError}</div>
              </div>
              <Button
                size="sm" variant="outline"
                onClick={onRetry} disabled={busy}
                data-testid="selfie-upload-retry"
              >
                Retry upload
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RadiusFlag({ today, radius }) {
  if (today?.check_in_within_radius === true) {
    return (
      <div className="mt-2 text-[11px] rounded-full inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1">
        <CheckCircle2 className="h-3 w-3" /> Within beat radius
      </div>
    );
  }
  if (today?.check_in_within_radius === false) {
    const d = today.check_in_distance_from_beat_m;
    return (
      <div className="mt-2 text-[11px] rounded-full inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-1">
        <AlertTriangle className="h-3 w-3" /> Outside beat by {d ? `${d}m` : "distance unknown"} · flagged
      </div>
    );
  }
  return null;
}

// Right-hand card of the "Day complete" screen: what was done today, instead of an empty
// punch-out photo box (no selfie is taken at punch-out).
function DaySummaryCard({ punchOutTs }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/home/today_dashboard").then((r) => setD(r.data)).catch(() => setD({}));
  }, []);
  const hm = (h) => {
    if (h == null) return "—";
    const m = Math.round(h * 60);
    return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
  };
  const row = (k, v) => (
    <div className="flex items-baseline justify-between gap-2 text-sm" key={k}>
      <span className="text-neutral-500 text-xs">{k}</span>
      <span className="font-semibold text-neutral-900 text-right">{v}</span>
    </div>
  );
  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid="attendance-day-summary">
      <CardContent className="p-3 space-y-1.5">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Today's work</div>
        {d === null ? <Loader2 className="h-4 w-4 animate-spin text-neutral-400" /> : (
          <>
            {row("Calls", `${d.total_unique_visited_today ?? 0}${d.total_calls_planned_today ? ` / ${d.total_calls_planned_today}` : ""}`)}
            {row("Productive", d.total_unique_productive_today ?? 0)}
            {row("Sales", `${(d.total_kg_productive_today ?? 0).toFixed(1)} kg`)}
            {row("Market hours", hm(d.working_hours_today))}
            {row("Punched out", fmtTime(punchOutTs))}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ThumbCard({ label, ref_ }) {
  return (
    <Card className="border-neutral-200 rounded-2xl">
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">{label}</div>
        {ref_ ? (
          <AuthImage refId={ref_} w={200} alt={label} className="w-full h-32 object-cover rounded-lg"
                     data-testid={`attendance-thumb-${label.toLowerCase().replace(/[^a-z]/g,"-")}`}
                     fallback={<div className="w-full h-32 bg-neutral-100 rounded-lg flex items-center justify-center text-[11px] text-neutral-400">Photo not available</div>} />
        ) : (
          <div className="w-full h-32 bg-neutral-100 rounded-lg flex items-center justify-center text-[11px] text-neutral-400">No photo</div>
        )}
      </CardContent>
    </Card>
  );
}
